import React, { useState, useEffect } from 'react';
import { Plus, Edit, AlertCircle, Store, Save, X } from 'lucide-react';
import { api } from '../services/api';
import { authService } from '../services/authService';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useAdminToast } from './admin/AdminToast';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const StoreManagement = () => {
  const [stores, setStores] = useState([]);
  const [bocs, setBocs] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { showSuccess, toastElement } = useAdminToast();
  const { confirm, confirmDialog } = useConfirmDialog();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    storeId: '',
    name: '',
    bocIdFk: '',
    enterpriseIdFk: '',
    companyId: '',
  });

  useEffect(() => {
    loadStores();
    loadBOCs();
    loadEnterprises();
  }, []);

  const loadStores = async () => {
    setError('');
    try {
      const response = await api.get('/admin/stores');
      setStores(response.data);
    } catch (error) {
      console.error('Error loading stores:', error);
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const loadBOCs = async () => {
    setError('');
    try {
      const response = await api.get('/admin/bocs');
      setBocs(response.data);
    } catch (error) {
      console.error('Error loading BOCs:', error);
    }
  };

  const loadEnterprises = async () => {
    setError('');
    try {
      const response = await api.get('/admin/enterprises');
      setEnterprises(response.data);
    } catch (error) {
      console.error('Error loading enterprises:', error);
    }
  };

  const handleCreateStore = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/admin/stores', formData);
      await loadStores();
      setShowCreateForm(false);
      resetForm();
      showSuccess(`Store "${formData.name || formData.storeId}" created`);
    } catch (error) {
      console.error('Error creating store:', error);
      setError(error.response?.data?.message || 'Failed to create store');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStore = async (e) => {
    e.preventDefault();
    if (!editingStore) return;
    setSubmitting(true);

    setError('');
    try {
      await api.put(`/admin/stores/${editingStore.id}`, formData);
      await loadStores();
      setEditingStore(null);
      setShowCreateForm(false);
      resetForm();
      showSuccess(`Store "${formData.name || formData.storeId}" updated`);
    } catch (error) {
      console.error('Error updating store:', error);
      setError(error.response?.data?.message || 'Failed to update store');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisableStore = async (id) => {
    const store = stores.find((s) => s.id === id);
    const label = store ? (store.name || store.storeId) : 'this store';
    const confirmed = await confirm({
      title: `Deactivate store "${label}"?`,
      message: `"${label}" will no longer be selectable elsewhere in the app.`,
      confirmLabel: 'Deactivate',
    });
    if (!confirmed) return;

    setError('');
    try {
      await api.delete(`/admin/stores/${id}`);
      await loadStores();
      showSuccess(`Store "${label}" disabled`);
    } catch (error) {
      console.error('Error disabling store:', error);
      setError(error.response?.data?.message || 'Failed to disable store');
    }
  };

  const startEdit = (store) => {
    setEditingStore(store);
    setError('');
    setFormData({
      storeId: store.storeId || '',
      name: store.name || '',
      bocIdFk: store.bocIdFk || '',
      enterpriseIdFk: store.enterpriseIdFk || '',
      companyId: store.companyId || '',
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      storeId: '',
      name: '',
      bocIdFk: '',
      enterpriseIdFk: '',
      companyId: '',
    });
  };

  if (loading) return <AdminLoading message="Loading stores..." />;

  return (
    <div className="space-y-6">
      {toastElement}
      {confirmDialog}
      <AdminPageHeader
        icon={Store}
        title="Store Management"
        subtitle="Manage internal stores mapped to BOCs"
        rightContent={
          !showCreateForm && (
            <AdminAddButton
              onClick={() => {
                setShowCreateForm(true);
                setEditingStore(null);
                setError('');
                resetForm();
              }}
              label="Add Store"
            />
          )
        }
      />

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center">
              {editingStore ? (
                <>
                  <Edit className="h-6 w-6 mr-2 text-blue-600" />
                  Edit Store
                </>
              ) : (
                <>
                  <Plus className="h-6 w-6 mr-2 text-blue-600" />
                  Create New Store
                </>
              )}
            </h3>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setEditingStore(null);
                setError('');
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={editingStore ? handleUpdateStore : handleCreateStore} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Store Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter store name"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Store ID</label>
              <input
                type="text"
                value={formData.storeId}
                onChange={(e) => setFormData({ ...formData, storeId: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter store ID"
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Enterprise</label>
              <select
                value={formData.enterpriseIdFk}
                onChange={(e) => setFormData({ ...formData, enterpriseIdFk: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={submitting}
              >
                <option value="">Select Enterprise</option>
                {enterprises.map((ent) => (
                  <option key={ent.id} value={ent.id}>
                    {ent.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">BOC</label>
              <select
                value={formData.bocIdFk}
                onChange={(e) => setFormData({ ...formData, bocIdFk: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
                disabled={submitting}
              >
                <option value="">Select BOC</option>
                {bocs.filter(b => b.enterpriseIdFk === Number(formData.enterpriseIdFk)).map((boc) => (
                  <option key={boc.id} value={boc.id}>
                    {boc.name ? `${boc.name} (${boc.bocId})` : boc.bocId}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company ID</label>
              <input
                type="text"
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                className="mt-1 block w-full px-3 py-2.5 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter company ID (BOC ID)"
                disabled={submitting}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingStore(null);
                  setError('');
                  resetForm();
                }}
                className="px-5 py-2.5 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting}
              >
                <X className="h-4 w-4 inline mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingStore ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 inline mr-2" />
                    {editingStore ? 'Update Store' : 'Create Store'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stores Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Store className="h-5 w-5 mr-2 text-gray-600" />
            Stores ({stores.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Store</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">BOC</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Enterprise</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Company ID</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stores.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <Store className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No stores found. Create one to get started.</p>
                  </td>
                </tr>
              ) : (
                stores.map((store) => (
                  <tr key={store.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{store.name || store.storeId || '-'}</div>
                      {store.name && <div className="text-xs text-gray-400">{store.storeId}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        const boc = bocs.find(b => b.id === store.bocIdFk);
                        return boc ? (boc.name || boc.bocId) : '-';
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {enterprises.find(e => e.id === store.enterpriseIdFk)?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{store.companyId || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                        store.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        <span className={`w-2 h-2 rounded-full mr-2 ${
                          store.active ? 'bg-green-500' : 'bg-red-500'
                        }`}></span>
                        {store.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <AdminRowActions align="start">
                        <AdminActionButton variant="edit" onClick={() => startEdit(store)} />
                        <AdminActionButton variant="deactivate" onClick={() => handleDisableStore(store.id)} />
                      </AdminRowActions>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StoreManagement;
